import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';

import { useAxios } from '@/hooks/useAxios';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useMy, UseMyOptions } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { Job, JobFilters, JobStatus, ResourceType, Statistics } from '@/types';
import {
  mergeStatus,
  UseExtendQueryOptions,
  useMergeStatus,
  useMutation,
} from '@/utils/api';
import { getJobStatus } from '@/utils/job';

// At the top of hooks/useJobs.ts
const getFailedComment = (job: Job) => {
  if (job.comment) {
    let trimmed = job.comment.substring(0, 45);
    if (trimmed.length < job.comment.length) {
      trimmed = trimmed + '...';
    }
    return trimmed;
  }
  return '';
};

export function useJobs(
  filter: JobFilters = {
    search: '',
    query: '',
    failedReason: '',
  }
) {
  // Use 0ms delay when clearing search, 300ms when typing
  // This prevents the "empty table" bug when clearing a search that returned no results
  const [debouncedSearch] = useDebounce(
    filter.search,
    filter.search === '' ? 0 : 300
  );

  const isSearched = Boolean(filter.search);
  const isRiskKey = Boolean(filter.riskKey);

  const { jobStats, status: jobStatsStatus } = useJobStats();

  const {
    data: jobsData,
    refetch: refreshJobsData,
    error: jobsDataError,
    status: jobsDataStatus,
    isFetchingNextPage: isFetchingJobsDataNextPage,
    fetchNextPage: fetchJobsDataNextPage,
    isFetching: isFetchingJobsData,
    hasNextPage: hasNextJobsDataPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: filter.query || '#job',
    },
    { enabled: !isSearched && !isRiskKey, refetchInterval: 5000 }
  );

  // Fetch the risk to get its source field (capability that found it)
  const {
    data: risks,
    status: riskStatus,
    error: riskError,
    isFetching: isFetchingRisk,
    refetch: refetchRisk,
  } = useMy(
    {
      resource: 'risk',
      query: {
        node: {
          labels: ['Risk'],
          filters: [
            { field: 'key', operator: '=', value: filter.riskKey, not: false },
          ],
        },
      },
    },
    { enabled: isRiskKey }
  );
  const risk = risks?.[0];

  // Use simple source-based query to fetch jobs (matches useCapabilityJobs pattern)
  // This is much simpler than the previous graph traversal approach which failed
  // because the source node in the graph query lacked required labels
  const {
    data: riskJobsData,
    status: riskJobsStatus,
    error: riskJobsError,
    isFetching: isFetchingRiskJobs,
    refetch: refetchRiskJobs,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: risk?.source ? `source:${risk.source}` : undefined,
    },
    {
      enabled: isRiskKey && !!risk?.source,
      refetchInterval: 5000,
    }
  );

  const riskJobs = useMemo(
    () => riskJobsData?.jobs || [],
    [riskJobsData?.jobs]
  );

  const {
    data: jobSearchByName,
    refetch: refreshJobSearchByName,
    status: jobSearchByNameStatus,
    error: jobSearchByNameError,
    isFetching: isFetchingjobSearchByName,
    fetchNextPage: jobSearchByNameFetchNextPage,
    isFetchingNextPage: jobSearchByNameIsFetchingNextPage,
    hasNextPage: jobSearchByNameHasNextPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: `name:${debouncedSearch}`,
    },
    {
      enabled: isSearched,
      refetchInterval: 5000,
      placeholderData: keepPreviousData,
    }
  );
  const {
    data: jobSearchByDns,
    refetch: refreshJobSearchByDns,
    status: jobSearchByDnsStatus,
    error: jobSearchByDnsError,
    isFetching: isFetchingjobSearchByDns,
    fetchNextPage: jobSearchByDnsFetchNextPage,
    isFetchingNextPage: jobSearchByDnsIsFetchingNextPage,
    hasNextPage: jobSearchByDnsHasNextPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: `#job#${debouncedSearch}`,
    },
    {
      enabled: isSearched,
      refetchInterval: 5000,
      placeholderData: keepPreviousData,
    }
  );
  const {
    data: jobSearchBySource,
    refetch: refreshJobSearchBySource,
    status: jobSearchBySourceStatus,
    error: jobSearchBySourceError,
    isFetching: isFetchingjobSearchBySource,
    fetchNextPage: jobSearchBySourceFetchNextPage,
    isFetchingNextPage: jobSearchBySourceIsFetchingNextPage,
    hasNextPage: jobSearchBySourceHasNextPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: `source:${debouncedSearch}`,
    },
    {
      enabled: isSearched,
      refetchInterval: 5000,
      placeholderData: keepPreviousData,
    }
  );

  const apiStatus = useMergeStatus(
    ...(debouncedSearch
      ? [jobSearchByNameStatus, jobSearchByDnsStatus, jobSearchBySourceStatus]
      : isRiskKey
        ? [riskStatus, riskJobsStatus]
        : [jobsDataStatus])
  );
  const isFetchingNextPage = debouncedSearch
    ? jobSearchByNameIsFetchingNextPage ||
      jobSearchByDnsIsFetchingNextPage ||
      jobSearchBySourceIsFetchingNextPage
    : isRiskKey
      ? false
      : isFetchingJobsDataNextPage;

  const error = debouncedSearch
    ? jobSearchByNameError || jobSearchByDnsError || jobSearchBySourceError
    : isRiskKey
      ? riskError || riskJobsError
      : jobsDataError;
  const fetchNextPage = useCallback(() => {
    if (debouncedSearch) {
      jobSearchByNameFetchNextPage();
      jobSearchByDnsFetchNextPage();
      jobSearchBySourceFetchNextPage();
    } else if (!isRiskKey) {
      fetchJobsDataNextPage();
    }
  }, [
    debouncedSearch,
    isRiskKey,
    jobSearchByNameFetchNextPage,
    jobSearchByDnsFetchNextPage,
    jobSearchBySourceFetchNextPage,
    fetchJobsDataNextPage,
  ]);
  const isFetching = debouncedSearch
    ? isFetchingjobSearchByName ||
      isFetchingjobSearchByDns ||
      isFetchingjobSearchBySource
    : isRiskKey
      ? isFetchingRisk || isFetchingRiskJobs
      : isFetchingJobsData;
  const hasNextPage = debouncedSearch
    ? jobSearchByNameHasNextPage ||
      jobSearchByDnsHasNextPage ||
      jobSearchBySourceHasNextPage
    : isRiskKey
      ? false
      : hasNextJobsDataPage;
  const refetch = debouncedSearch
    ? () => {
        refreshJobSearchByName();
        refreshJobSearchByDns();
        refreshJobSearchBySource();
      }
    : isRiskKey
      ? () => {
          refetchRisk();
          refetchRiskJobs();
        }
      : refreshJobsData;

  const data: Job[] = useMemo(() => {
    const data = debouncedSearch
      ? [
          ...(jobSearchByName?.jobs || []),
          ...(jobSearchByDns?.jobs || []),
          ...(jobSearchBySource?.jobs || []),
        ].reduce((acc, asset: Job) => {
          if (!acc.find(a => a.key === asset.key)) {
            acc.push(asset);
          }
          return acc;
        }, [] as Job[])
      : isRiskKey
        ? riskJobs
        : jobsData?.jobs || [];

    return data;
  }, [
    debouncedSearch,
    isRiskKey,
    jobSearchByName?.jobs,
    jobSearchByDns?.jobs,
    jobSearchBySource?.jobs,
    jobsData?.jobs,
    riskJobs,
  ]);

  const failedJobStats = useMemo(() => {
    const failedJobStats = data?.reduce(
      (acc, job) => {
        if (getJobStatus(job) === JobStatus.Fail) {
          const comment = getFailedComment(job);

          if (comment) {
            return {
              ...acc,
              [comment]: (acc[comment] || 0) + 1,
            };
          }
        }
        return acc;
      },
      {} as Record<string, number>
    );

    return Object.entries(failedJobStats ?? {}).map(([reason, count]) => ({
      label: reason,
      value: reason,
      count: count,
    }));
  }, [data]);

  // Derived value: true when we need to fetch more pages to reach minimum display threshold
  const needsMoreData = hasNextPage && data.length < 50;

  // Status shows pending during fetch cycle OR when more data is needed
  const status = isFetching || needsMoreData ? 'pending' : apiStatus;

  // Auto-fetch next page when we need more data (no setState - derived from needsMoreData)
  useEffect(() => {
    if (!isFetching && needsMoreData) {
      fetchNextPage();
    }
  }, [isFetching, needsMoreData, fetchNextPage]);

  const filteredData = useMemo(() => {
    return filter.failedReason
      ? data.filter(job => getFailedComment(job) === filter.failedReason)
      : data;
  }, [data, filter.failedReason]);

  return {
    data: filteredData,
    refetch,
    status,
    fetchNextPage,
    error,
    isFetchingNextPage,
    isFetching,
    failedJobStats,
    hasNextPage,
    jobStats,
    jobStatsStatus,
  };
}

export function useReRunJob() {
  const axios = useAxios();

  const { invalidate: invalidateJobs } = useMy(
    { resource: 'job' },
    { enabled: false }
  );

  return useMutation({
    defaultErrorMessage: 'Failed to re run job',
    mutationFn: ({ jobKey }: { jobKey: string }) => {
      const promise = axios.post(`/job/`, {
        key: jobKey,
      });

      toast.promise(promise, {
        loading: 'Processing Scan Request',
        success: 'Scan Request Processed',
        error: 'Failed to process Scan Request',
      });

      return promise;
    },
    onSuccess: () => {
      invalidateJobs();
    },
  });
}

export function useCancelJob() {
  const axios = useAxios();

  const { invalidate: invalidateJobs } = useMy(
    { resource: 'job' },
    { enabled: false }
  );

  return useMutation({
    defaultErrorMessage: 'Failed to cancel job',
    mutationFn: ({ jobKey }: { jobKey: string }) => {
      const promise = axios.delete('/job/', {
        data: {
          key: jobKey,
        },
      });

      toast.promise(promise, {
        loading: 'Cancelling job...',
        success: 'Cancellation requested',
        error: 'Failed to cancel job',
      });

      return promise;
    },
    onSuccess: () => {
      invalidateJobs();
    },
  });
}

export function useBulkReRunJob({ email }: { email?: string } = {}) {
  const axios = useAxios({ email });

  const { invalidate: invalidateJobs } = useMy(
    { resource: 'job' },
    { enabled: false }
  );

  return useMutation({
    defaultErrorMessage: 'Failed to re run job',
    mutationFn: async (
      jobs: {
        jobKey?: string;
      }[]
    ) => {
      const promises = jobs
        .map(async ({ jobKey }) => {
          const { data } = await axios.post(`/job/`, {
            key: jobKey,
          });

          return data;
        })
        .map(promise => promise.catch(error => error));

      const singlePromise = Promise.all(promises);

      toast.promise(singlePromise, {
        loading: 'Processing Scan Request',
        success: 'Scan Request Processed',
        error: 'Failed to process Scan Request',
      });

      const response = await singlePromise;

      const validResults = response.filter(
        result => !(result instanceof Error)
      );

      if (validResults.length > 0) {
        invalidateJobs();
      }

      queryClient.invalidateQueries({
        queryKey: ['riskJobs'],
      });

      return response;
    },
  });
}

type JobWithFailedCount = Job & {
  failedJobsCount: number;
  failedJobSource: string[];
};

export const useJobsStatus = (
  attributeJobMap: Record<string, string>,
  options?: UseExtendQueryOptions<JobWithFailedCount>
) => {
  const axios = useAxios();

  return useQueries({
    // defaultErrorMessage: 'Failed to fetch job status',
    queries: Object.values(attributeJobMap).map(jobKey => {
      return {
        ...options,
        queryKey: getQueryKey.getMy('job', jobKey),
        queryFn: async () => {
          const res = await axios.get(`/my`, {
            params: {
              key: jobKey,
            },
          });

          const allSuccessfulJobs = res.data.jobs.every(
            (job: Job) => getJobStatus(job) === JobStatus.Pass
          );
          const failedJobs = res.data.jobs.filter(
            (job: Job) => getJobStatus(job) === JobStatus.Fail
          );

          return allSuccessfulJobs
            ? res.data.jobs[0]
            : {
                ...res.data.jobs[0],
                failedJobsCount: failedJobs.length,
                failedJobSource: failedJobs.map((job: Job) => job.source),
              };
        },
      };
    }),
    combine: results => {
      return {
        data: Object.keys(attributeJobMap).reduce(
          (acc, current, index) => ({
            ...acc,
            [current]: results[index].data,
          }),
          {}
        ) as Record<string, JobWithFailedCount>,
        status: mergeStatus(...results.map(result => result.status)),
      };
    },
  });
};

export interface JobStatsResult {
  [JobStatus.Fail]: number;
  [JobStatus.Pass]: number;
  [JobStatus.Queued]: number;
  [JobStatus.Running]: number;
  [JobStatus.AsyncRunning]: number;
  [JobStatus.AsyncReturning]: number;
  total: number;
}

/**
 * @deprecated This function is deprecated and should not be used.
 * Use `useJobStats()` hook instead which fetches pre-aggregated statistics
 * from the backend endpoint. This function performed client-side aggregation
 * which is now handled by the backend cron job.
 *
 * This function is kept for backward compatibility but will be removed in a future version.
 */
export const getJobStats = (stats: Statistics): JobStatsResult => {
  const jobStatsFn = () => {
    const rawStats = Object.entries(stats).reduce(
      (acc, [key, value]) => {
        Object.values(JobStatus).forEach(jobKey => {
          if (key.endsWith(jobKey)) {
            acc[jobKey] = (acc[jobKey] || 0) + (value as number);
          }
        });

        return acc;
      },
      {
        JF: 0,
        JP: 0,
        JQ: 0,
        JR: 0,
        JRA: 0,
        JQA: 0,
      } as Record<JobStatus, number>
    );

    // Merge async statuses with their sync counterparts
    return {
      [JobStatus.Fail]: rawStats[JobStatus.Fail],
      [JobStatus.Pass]: rawStats[JobStatus.Pass],
      [JobStatus.Queued]:
        rawStats[JobStatus.Queued] + rawStats[JobStatus.AsyncReturning],
      [JobStatus.Running]:
        rawStats[JobStatus.Running] + rawStats[JobStatus.AsyncRunning],
      // Keep async statuses as 0 since they're merged
      [JobStatus.AsyncRunning]: 0,
      [JobStatus.AsyncReturning]: 0,
    };
  };

  const jobStats = jobStatsFn();

  return {
    ...jobStats,
    total: Object.values(jobStats).reduce((acc, current) => acc + current, 0),
  };
};

/**
 * Fetches pre-aggregated job statistics from the backend statistic endpoint.
 *
 * The backend creates these statistics hourly via cron job and stores them
 * with the key pattern: #statistic#job#status#now#status
 *
 * Response format from backend (/cmd/cron/count/main.go lines 377-388):
 * {
 *   "statistics": [{
 *     "data": {
 *       "counts": {
 *         "success": 2155,  // JP (Pass) jobs
 *         "failed": 167,    // JF (Fail) jobs
 *         "queued": 0,      // JQ + JQA (Queued + AsyncReturning) jobs
 *         "running": 4      // JR + JRA (Running + AsyncRunning) jobs
 *       }
 *     }
 *   }]
 * }
 *
 * @param options - Optional React Query options with customer email for impersonation
 * @returns Job statistics with status codes matching JobStatus enum
 */
export const useJobStats = (options?: UseMyOptions<'statistic', false>) => {
  const { data: statistics = [], status } = useMy(
    {
      resource: 'statistic',
      query: '#job#status#now',
    },
    {
      ...options,
      // Default cache settings (can be overridden by options)
      staleTime: options?.staleTime ?? 30000, // Cache for 30 seconds
      gcTime: options?.gcTime ?? 300000, // Keep in cache for 5 minutes
      refetchInterval: options?.refetchInterval ?? 30000, // Auto-refresh every 30 seconds
      refetchOnMount: options?.refetchOnMount ?? false,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    }
  );

  const jobStats = useMemo(() => {
    // Backend returns pre-aggregated counts in the first statistic
    const stat = statistics[0];
    const counts = stat?.data?.counts || {};

    // Map backend response keys to frontend JobStatus enum values
    // Backend uses: success, failed, queued, running (see /cmd/cron/count/main.go:378-383)
    // Frontend uses: JP, JF, JQ, JR, JRA, JQA
    return {
      [JobStatus.Pass]: counts.success || 0,
      [JobStatus.Fail]: counts.failed || 0,
      [JobStatus.Queued]: counts.queued || 0,
      [JobStatus.Running]: counts.running || 0,
      // Async statuses are already merged in backend, so set to 0
      [JobStatus.AsyncRunning]: 0,
      [JobStatus.AsyncReturning]: 0,
      total:
        (counts.success || 0) +
        (counts.failed || 0) +
        (counts.queued || 0) +
        (counts.running || 0),
    };
  }, [statistics]);

  return {
    jobStats,
    status,
  };
};
